import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronDown, FiChevronUp } = FiIcons;

const FAQSection = () => {
  const [openIndex, setOpenIndex] = React.useState(null);

  const faqs = [
    {
      question: "What's the typical cost savings when switching to 3CX?",
      answer: "Most businesses save between 30-50% on their monthly phone costs when switching to 3CX from traditional providers. For example, a 20-user system that typically costs $800-1000/month with RingCentral or Vonage would cost $400-500/month with 3CX, including local support and all enterprise features."
    },
    {
      question: "How long does it take to switch from our current provider?",
      answer: "The typical migration takes 2-4 weeks, with zero downtime during the transition. We handle everything including number porting, user setup, and training. We maintain parallel systems during the transition to ensure continuous communication capabilities."
    },
    {
      question: "Do we need to purchase new phones?",
      answer: "Not necessarily. 3CX is compatible with most modern IP phones, including Yealink, Polycom, and Snom devices. We can assess your existing hardware and often repurpose it. When new phones are needed, we provide competitive options with flexible payment terms."
    },
    {
      question: "What happens if we need support after hours?",
      answer: "We provide 24/7 emergency support for critical issues. Our local team is based in the Hudson Valley and typically responds within 15 minutes. For non-emergency issues, we maintain standard support hours from 8AM to 6PM ET, Monday through Friday."
    },
    {
      question: "Is 3CX HIPAA compliant?",
      answer: "Yes, 3CX can be configured to meet HIPAA requirements. We provide secure call encryption, detailed audit logs, and role-based access control. We'll help you implement the necessary security measures and provide documentation for your compliance records."
    },
    {
      question: "Can we keep our existing phone numbers?",
      answer: "Yes, we handle the entire number porting process. You can keep all your existing numbers, including toll-free numbers. We also provide temporary numbers during the transition period to ensure continuous service."
    },
    {
      question: "What kind of internet connection do we need?",
      answer: "3CX works well with standard business internet connections. For optimal performance, we recommend at least 100Mbps download/upload speeds. We'll assess your current internet setup and can recommend improvements if needed. We also implement QoS (Quality of Service) to prioritize voice traffic."
    },
    {
      question: "Do you provide employee training?",
      answer: "Yes, we provide comprehensive training for all users. This includes live group sessions, one-on-one training for administrators, and access to our video library. We also provide quick reference guides and ongoing support for new employees."
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="w-4/5 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to common questions about switching to 3CX
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <button
                className={`w-full text-left p-6 rounded-lg ${
                  openIndex === index ? 'bg-gray-50' : 'bg-white'
                } border border-gray-200 hover:border-primary-300 transition-colors`}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {faq.question}
                  </h3>
                  <SafeIcon
                    icon={openIndex === index ? FiChevronUp : FiChevronDown}
                    className={`h-5 w-5 transform transition-transform ${
                      openIndex === index ? 'text-primary-600' : 'text-gray-400'
                    }`}
                  />
                </div>
                {openIndex === index && (
                  <p className="mt-4 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                )}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;